'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useListingScrollRestore } from '@/core/hooks/useListingScrollRestore';
import { ChevronLeft, ChevronRight, SlidersHorizontal, X } from 'lucide-react';
import { ProductCard } from '@/modules/shop/components/product/product-card';
import { SidebarFilters } from './sidebar-filters';
import { CatalogToolbar } from './catalog-toolbar';
import { CatalogProvider, useCatalogContext } from '@/core/components/Builder/CatalogContext';
import { cn } from '@/core/utils/cn';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { ItemListTracker, SearchTracker } from '@/core/components/Analytics/EcommerceTrackers';
import { fetchAPI } from '@/core/api/client';
import { activeFilterCount, appendFacetFilters, filtersKey, type FiltersState } from './catalog-filter-utils';

interface CatalogContainerProps {
    products: any[];
    subcategories?: Array<{ id: number; name: string; slug: string }>;
    categoryPath?: string;
    categoryId?: number | null;
    sidebarPosition?: 'left' | 'right';
    columns?: 2 | 3 | 4;
    showFilters?: boolean;
    initialQuery?: string;
    initialFilters?: FiltersState;
    initialSort?: string;
    totalCount?: number;
    currentPage?: number;
    pageSize?: number;
}

const COLUMNS_CLASS: Record<number, string> = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
};

export function CatalogContainer(props: CatalogContainerProps) {
    return (
        <CatalogProvider
            subcategories={props.subcategories || []}
            initialQuery={props.initialQuery}
            initialFilters={props.initialFilters}
            initialPage={props.currentPage || 1}
            initialTotal={props.totalCount ?? props.products.length}
            initialSort={props.initialSort || ''}
            categoryId={props.categoryId || null}
        >
            <CatalogContainerContent {...props} />
        </CatalogProvider>
    );
}

function CatalogContainerContent({ products, categoryPath = '', sidebarPosition = 'left', columns = 4, showFilters = true, pageSize = 24 }: CatalogContainerProps) {
    const ctx = useCatalogContext()!;
    const { filters, sort, currentPage, setCurrentPage, categoryId, setProductCount } = ctx;
    const { t, currency } = useTranslation();
    const [items, setItems] = useState(products);
    const [loading, setLoading] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const previousFilterKey = useRef(`${filtersKey(filters)}|${sort}`);
    const currentFilterKey = `${filtersKey(filters)}|${sort}`;
    // Products are refetched right after mounting, so the back-navigation position is restored
    // after that load.
    useListingScrollRestore(true, loading, () => true);

    useEffect(() => {
        if (previousFilterKey.current !== currentFilterKey) {
            previousFilterKey.current = currentFilterKey;
            if (currentPage !== 1) setCurrentPage(1);
        }
    }, [currentFilterKey, currentPage, setCurrentPage]);

    useEffect(() => {
        let active = true;
        const params = new URLSearchParams({
            status: 'published',
            skip: String((currentPage - 1) * pageSize),
            limit: String(pageSize),
            sort,
            currency,
        });
        const effectiveCategoryId = filters.subcategoryId ?? categoryId;
        if (effectiveCategoryId) params.set('category_id', String(effectiveCategoryId));
        if (filters.query.trim()) params.set('search', filters.query.trim());
        if (filters.minPrice) params.set('min_price', filters.minPrice);
        if (filters.maxPrice) params.set('max_price', filters.maxPrice);
        appendFacetFilters(params, filters.facets);

        const countParams = new URLSearchParams(params);
        ['skip', 'limit', 'sort', 'currency'].forEach((key) => countParams.delete(key));
        setLoading(true);
        Promise.all([
            fetchAPI(`/products?${params.toString()}`),
            fetchAPI(`/products/count?${countParams.toString()}`).catch(() => ({ total: 0 })),
        ]).then(([nextItems, count]) => {
            if (!active) return;
            setItems(Array.isArray(nextItems) ? nextItems : []);
            setProductCount(typeof count?.total === 'number' ? count.total : 0);
        }).catch(() => {
            if (active) setItems([]);
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [filters.query, filters.minPrice, filters.maxPrice, filters.subcategoryId, filters.facets, sort, currentPage, categoryId, currency, pageSize, setProductCount]);

    const activeCount = activeFilterCount(ctx.filters);
    const totalPages = Math.ceil(ctx.productCount / pageSize);
    const columnsClass = COLUMNS_CLASS[columns] || COLUMNS_CLASS[4];

    const getPageUrl = (page: number) => {
        if (typeof window === 'undefined') return page > 1 ? `?strona=${page}` : '?';
        const url = new URL(window.location.href);
        if (page > 1) url.searchParams.set('strona', String(page));
        else {
            url.searchParams.delete('strona');
            url.searchParams.delete('page');
        }
        return `${url.pathname}${url.search}`;
    };

    const sidebar = showFilters ? (
        <div className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-6 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                <SidebarFilters initialFilters={ctx.filters} onFiltersChange={ctx.setFilters} subcategories={ctx.subcategories} productCount={ctx.productCount} categoryId={ctx.categoryId} onFacetsChange={ctx.setFacets} />
            </div>
        </div>
    ) : null;

    return (
        <div className="w-full">
            <ItemListTracker products={items} listId={categoryPath ? `category_${categoryPath}` : 'all_products'} listName={categoryPath || 'all_products'} currency={currency || 'PLN'} />
            <SearchTracker query={ctx.filters.query} />

            {showFilters && (
                <div className="mb-4 flex items-center justify-between lg:hidden">
                    <button type="button" onClick={() => setMobileOpen(true)} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm">
                        <SlidersHorizontal className="h-4 w-4" />
                        {t('filters.title', 'Filtry')}
                        {activeCount > 0 && <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[9px] font-black text-white">{activeCount}</span>}
                    </button>
                    <span className="text-sm text-slate-500">{t('category.products_count', '{n} produktów').replace('{n}', String(ctx.productCount))}</span>
                </div>
            )}

            {showFilters && (
                <>
                    <div className={cn('fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity lg:hidden', mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0')} onClick={() => setMobileOpen(false)} />
                    <div className={cn('fixed inset-y-0 left-0 z-50 flex w-80 max-w-[88vw] flex-col bg-white shadow-2xl transition-transform lg:hidden', mobileOpen ? 'translate-x-0' : '-translate-x-full')} role="dialog" aria-modal="true" aria-label={t('filters.title', 'Filtry')}>
                        <div className="flex items-center justify-between border-b p-4">
                            <span className="flex items-center gap-2 text-sm font-black uppercase tracking-wider"><SlidersHorizontal className="h-4 w-4" />{t('filters.title', 'Filtry')}</span>
                            <button type="button" onClick={() => setMobileOpen(false)} className="rounded-lg p-2 hover:bg-slate-100" aria-label={t('common.close', 'Zamknij')}><X className="h-4 w-4" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5">
                            <SidebarFilters initialFilters={ctx.filters} onFiltersChange={ctx.setFilters} subcategories={ctx.subcategories} productCount={ctx.productCount} categoryId={ctx.categoryId} onFacetsChange={ctx.setFacets} hideHeader />
                        </div>
                        <div className="border-t bg-slate-50 p-4">
                            <button type="button" onClick={() => setMobileOpen(false)} className="w-full rounded-xl bg-slate-900 py-3 text-sm font-bold text-white">
                                {t('filters.show_products', 'Pokaż {n} produktów').replace('{n}', String(ctx.productCount))}
                            </button>
                        </div>
                    </div>
                </>
            )}

            <div className={cn('flex gap-8', sidebarPosition === 'right' && 'flex-row-reverse')}>
                {sidebar}
                <div className="min-w-0 flex-1">
                    <CatalogToolbar />
                    {loading ? (
                        <div className={`grid grid-cols-1 ${columnsClass} gap-6`} aria-busy="true">
                            {Array.from({ length: Math.min(pageSize, 8) }, (_, index) => <div key={index} className="aspect-[3/4] animate-pulse rounded-2xl bg-slate-100" />)}
                        </div>
                    ) : items.length ? (
                        <>
                            <div className={`grid grid-cols-1 ${columnsClass} gap-6`}>
                                {items.map((product: any, index: number) => <ProductCard key={product.id} product={product} priority={index < 2} />)}
                            </div>
                            {totalPages > 1 && (
                                <div className="mt-12 flex items-center justify-center gap-2">
                                    <a href={ctx.currentPage > 1 ? getPageUrl(ctx.currentPage - 1) : '#'} className={cn('flex h-9 items-center gap-1 rounded-xl border border-slate-200 px-3 text-sm font-medium', ctx.currentPage === 1 && 'pointer-events-none opacity-40')}><ChevronLeft className="h-4 w-4" /><span className="hidden sm:inline">{t('pagination.prev', 'Poprzednia')}</span></a>
                                    {Array.from({ length: totalPages }, (_, index) => index + 1).filter((page) => totalPages <= 5 || page === 1 || page === totalPages || Math.abs(page - ctx.currentPage) <= 1).map((page) => (
                                        <a key={page} href={getPageUrl(page)} aria-current={ctx.currentPage === page ? 'page' : undefined} className={cn('flex h-9 w-9 items-center justify-center rounded-xl text-sm font-bold', ctx.currentPage === page ? 'bg-slate-900 text-white' : 'border border-slate-200 bg-white')}>{page}</a>
                                    ))}
                                    <a href={ctx.currentPage < totalPages ? getPageUrl(ctx.currentPage + 1) : '#'} className={cn('flex h-9 items-center gap-1 rounded-xl border border-slate-200 px-3 text-sm font-medium', ctx.currentPage === totalPages && 'pointer-events-none opacity-40')}><span className="hidden sm:inline">{t('pagination.next', 'Następna')}</span><ChevronRight className="h-4 w-4" /></a>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 py-20 text-center">
                            <div className="mb-4 text-4xl">🔍</div>
                            <p className="mb-2 text-lg font-bold">{t('filters.no_results_title', 'Brak wyników')}</p>
                            <p className="mx-auto max-w-xs text-sm text-slate-500">{t('filters.no_results_hint', 'Zmień kryteria wyszukiwania lub wyczyść filtry, by zobaczyć więcej produktów.')}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
